import { useEffect } from 'react'

// TechTouch カスタムプロパティ UUID 定数
const TECHTOUCH_UUIDS = {
  COMPANY_ID: '08b20f8c-fd4a-4c33-a76a-317c3910d897',
  WAREHOUSE_ID: '11febf5e-5aa5-4a33-9d63-2a48f5dbecf1',
  TENANT_ID: '4260182d-8263-4f8d-9bfc-721ccadc2c82',
  USER_ROLE: '260f3ba3-fd59-4041-8755-ccb0ed540ea5',
  USER_ID: 'a22cb755-21fc-4e9e-a813-adb487684306',
} as const

// Window 型定義
declare global {
  interface Window {
    TechtouchAdmin?: {
      customProperty: {
        set: (uuid: string, value: string) => void
        get: (uuid: string) => string
        delete: (uuid: string) => void
        keys: () => string[]
      }
    }
  }
}

/**
 * TechTouch カスタムプロパティを設定するカスタムフック
 */
export const useTechTouch = () => {
  const isTechTouchAvailable = (): boolean => {
    return !!(window.TechtouchAdmin && window.TechtouchAdmin.customProperty)
  }

  const setCustomProperty = (uuid: string, value: string | number | null | undefined): void => {
    if (!isTechTouchAvailable()) {
      console.warn('TechTouch API is not available')
      return
    }

    const stringValue = value != null ? String(value) : ''

    try {
      window.TechtouchAdmin!.customProperty.set(uuid, stringValue)
    } catch (error) {
      console.error('[TechTouch] Failed to set custom property:', error)
    }
  }

  return {
    isTechTouchAvailable,
    setCompanyId: (value: string | number | null | undefined) =>
      setCustomProperty(TECHTOUCH_UUIDS.COMPANY_ID, value),
    setWarehouseId: (value: string | number | null | undefined) =>
      setCustomProperty(TECHTOUCH_UUIDS.WAREHOUSE_ID, value),
    setTenantId: (value: string | number | null | undefined) =>
      setCustomProperty(TECHTOUCH_UUIDS.TENANT_ID, value),
    setUserRole: (value: string | null | undefined) =>
      setCustomProperty(TECHTOUCH_UUIDS.USER_ROLE, value),
    setUserId: (value: string | number | null | undefined) =>
      setCustomProperty(TECHTOUCH_UUIDS.USER_ID, value),
  }
}

/**
 * Redux state を監視して自動的に TechTouch を更新するフック
 */
export const useTechTouchSync = (
  companyId: string | number | null | undefined,
  warehouseId: string | number | null | undefined,
  tenantId: string | number | null | undefined,
  userRole: string | null | undefined,
  userId: string | number | null | undefined
) => {
  const { setCompanyId, setWarehouseId, setTenantId, setUserRole, setUserId } = useTechTouch()

  useEffect(() => {
    if (companyId != null) {
      setCompanyId(companyId)
    }
  }, [companyId, setCompanyId])

  useEffect(() => {
    if (warehouseId != null) {
      setWarehouseId(warehouseId)
    }
  }, [warehouseId, setWarehouseId])

  useEffect(() => {
    if (tenantId != null) {
      setTenantId(tenantId)
    }
  }, [tenantId, setTenantId])

  useEffect(() => {
    if (userRole) {
      setUserRole(userRole)
    }
  }, [userRole, setUserRole])

  useEffect(() => {
    if (userId != null) {
      setUserId(userId)
    }
  }, [userId, setUserId])
}
